<script lang="ts">
	import chevronLeftIcon from "@core/assets/icons/chevron-left.svg?raw";
	import chevronRightIcon from "@core/assets/icons/chevron-right.svg?raw";
	import searchPlusIcon from "@core/assets/icons/search-plus.svg?raw";
	import type { Product } from "@core/schema/Product.js";
	import translations from "../../templates/product.translations.json" with { type: "json" };
	import { getTranslate } from "../../utils/getTranslate.js";
	import Icon from "../common/Icon.svelte";
	import Media from "../common/Media.svelte";

	const {
		product,
	}: {
		product: Product;
	} = $props();

	const breakpoint = 768; // Define the breakpoint for responsive behavior

	let selectedImageIndex = $state(0);
	let isZoomed = $state(false);
	let zoomPosition = $state({ x: 0, y: 0 });
	let thumbnailScrollPosition = $state(0);
	const translate = getTranslate(translations);
	const visibleThumbnails = $state(typeof window !== "undefined" && window.innerWidth >= breakpoint ? 3 : 7);

	$effect(() => {
		if (product.gallery.length > visibleThumbnails) {
			const halfVisible = Math.floor(visibleThumbnails / 2);
			let targetPosition = selectedImageIndex - halfVisible;
			targetPosition = Math.max(0, Math.min(targetPosition, product.gallery.length - visibleThumbnails));
			thumbnailScrollPosition = targetPosition;
		}
	});
</script>

<div class="flex flex-col md:w-[290px] md:max-w-[290px] gap-2">
	{#if product.gallery.length > 0}
		{@const currentImage = product.gallery[selectedImageIndex] ?? product.gallery[0]}
		{#if currentImage}
			<div class="relative w-full aspect-square rounded-2xl overflow-hidden bg-gray-50 border group">
				<div
					class="relative w-full h-full overflow-hidden md:cursor-crosshair"
					role="img"
					aria-label={translate("zoomAlt")}
					onmousemove={(e: MouseEvent): void => {
						if (window.innerWidth >= 768) {
							const target = e.currentTarget as HTMLDivElement;
							const rect = target.getBoundingClientRect();
							const x = ((e.clientX - rect.left) / rect.width) * 100;
							const y = ((e.clientY - rect.top) / rect.height) * 100;
							zoomPosition = { x, y };
							isZoomed = true;
						}
					}}
					onmouseleave={(): void => {
						isZoomed = false;
					}}
				>
					<div
						class="w-full h-full transition-transform duration-200 ease-out"
						style="transform: {isZoomed
							? 'scale(2)'
							: 'scale(1)'}; transform-origin: {zoomPosition.x}% {zoomPosition.y}%;"
					>
						<Media
							wrapperClass="w-full h-full"
							class="object-cover w-full h-full"
							height={290}
							width={300}
							alt={product.name}
							source={currentImage}
							lightboxWidth={1000}
							lightbox="product-gallery"
						/>
					</div>

					<div
						class="absolute top-4 right-4 bg-black bg-opacity-70 text-white text-[11px]/[1] px-2 py-1 rounded-lg opacity-0 md:group-hover:opacity-100 transition-opacity hidden md:flex items-center gap-2"
					>
						<Icon icon={searchPlusIcon} class="w-4 h-4" />
						<span>{translate("zoomIndicator")}</span>
					</div>

					{#if isZoomed}
						<div class="absolute bottom-2 left-2 hidden md:block">
							<div class="relative w-16 h-16 border border-white rounded-lg overflow-hidden shadow-lg bg-white">
								<Media
									wrapperClass="w-full h-full"
									class="object-cover w-full h-full"
									height={80}
									width={80}
									alt={translate("overviewAlt").replace("{productName}", product.name)}
									source={currentImage}
								/>

								<div
									class="absolute border-2 border-zms-blue bg-zms-blue-dark bg-opacity-20"
									style="
										left: {Math.max(0, Math.min(60, zoomPosition.x * 0.96 - 12))}%;
										top: {Math.max(0, Math.min(60, zoomPosition.y * 0.96 - 12))}%;
										width: 40%;
										height: 40%;
									"
								></div>
							</div>
						</div>
					{/if}

					{#if isZoomed}
						<div
							class="absolute pointer-events-none hidden md:block"
							style="left: {zoomPosition.x}%; top: {zoomPosition.y}%; transform: translate(-50%, -50%);"
						>
							<div class="w-6 h-6 border-2 border-white rounded-full shadow-lg opacity-60"></div>
						</div>
					{/if}
				</div>
			</div>
		{/if}

		{#if product.gallery.length > 1}
			<div>
				<div class="relative">
					<div class="overflow-hidden rounded-lg">
						<div
							class="flex gap-3 transition-transform duration-300 ease-out"
							style="transform: translateX(-{thumbnailScrollPosition * (96 + 12)}px);"
						>
							{#each product.gallery as image, index (index)}
								<button
									class="relative flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 transition-all duration-200"
									class:border-zms-blue={selectedImageIndex === index}
									class:border-gray-200={selectedImageIndex !== index}
									class:shadow-lg={selectedImageIndex === index}
									class:ring-1={selectedImageIndex === index}
									class:ring-zms-blue={selectedImageIndex === index}
									class:ring-opacity-50={selectedImageIndex === index}
									onclick={(): void => {
										selectedImageIndex = index;
									}}
									aria-label={translate("selectImage").replace("{index}", String(index + 1))}
								>
									<Media
										wrapperClass="w-full h-full"
										class="object-cover w-full h-full"
										height={96}
										width={96}
										alt={translate("thumbnailAlt")
											.replace("{productName}", product.name)
											.replace("{index}", String(index + 1))}
										source={image}
									/>

									{#if selectedImageIndex === index}
										<div
											class="absolute inset-0 bg-gradient-to-t from-zms-blue/30 to-transparent flex items-end justify-center pb-2"
										>
											<div class="w-2 h-2 bg-white rounded-full shadow-md"></div>
										</div>
									{/if}

									<div
										class="absolute top-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1.5 py-0.5 rounded text-center min-w-[20px]"
									>
										{index + 1}
									</div>
								</button>
							{/each}
						</div>
					</div>
					<div class="w-full flex justify-between">
						<div class="text-sm text-gray-500 mt-1">
							{selectedImageIndex + 1} /
							{product.gallery.length}
						</div>
						<div class="flex items-center gap-2 mt-2">
							{#if product.gallery.length > visibleThumbnails}
								<div class="hidden md:flex gap-2">
									<button
										class="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
										disabled={selectedImageIndex === 0}
										aria-label={translate("previousImage")}
										onclick={(): void => {
											if (selectedImageIndex > 0) {
												selectedImageIndex--;
											}
										}}
									>
										<Icon icon={chevronLeftIcon} class="w-4 h-4" />
									</button>
									<button
										class="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
										disabled={selectedImageIndex === product.gallery.length - 1}
										aria-label={translate("nextImage")}
										onclick={(): void => {
											if (selectedImageIndex < product.gallery.length - 1) {
												selectedImageIndex++;
											}
										}}
									>
										<Icon icon={chevronRightIcon} class="w-4 h-4" />
									</button>
								</div>
							{/if}
						</div>
					</div>
				</div>

				<div class="flex justify-center gap-3 md:hidden">
					<button
						class="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
						disabled={selectedImageIndex === 0}
						aria-label={translate("previousImage")}
						onclick={(): void => {
							if (selectedImageIndex > 0) {
								selectedImageIndex--;
							}
						}}
					>
						<Icon icon={chevronLeftIcon} class="w-4 h-4" />
						<span class="text-sm">{translate("previous")}</span>
					</button>

					<button
						class="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
						disabled={selectedImageIndex === product.gallery.length - 1}
						aria-label={translate("nextImage")}
						onclick={(): void => {
							if (selectedImageIndex < product.gallery.length - 1) {
								selectedImageIndex++;
							}
						}}
					>
						<span class="text-sm">{translate("next")}</span>
						<Icon icon={chevronRightIcon} class="w-4 h-4" />
					</button>
				</div>
			</div>
		{/if}
	{/if}
</div>
